import React, { useState, useEffect, useMemo, useCallback } from "react";
import { collection, getDocs, doc, setDoc } from "firebase/firestore";
import {
  Shield,
  Save,
  Plus,
  AlertTriangle,
  Users,
  Settings,
  Edit2,
  X,
  Check,
  Copy,
  ClipboardPaste,
  BarChart3,
  Users as UsersIcon,
  Lock,
  Unlock,
  HelpCircle,
  BookOpen,
  Download,
  Upload,
  Crown,
  Eye,
} from "lucide-react";
import { db } from "../../firebase";
import PermissionGate from "../../components/auth/PermissionGate";
import Button from "../../components/ui/Button";
import Alert from "../../components/ui/Alert";
import RoleCard from "./components/RoleCard";
import AdvancedPermissionTable from "./components/AdvancedPermissionTable";
import AddRoleModal from "./components/AddRoleModal";
import EditRoleModal from "./components/EditRoleModal";
import { useAdvancedPermissions } from "../../hooks/useAdvancedPermissions";
import AdvancedRolesService from "../../services/advancedRolesService";
import { ADVANCED_ROLES_CONFIG } from "../../config/advancedRolesConfig";
import "./RolesManagement.css";

export function AdvancedRolesManagementPage({ currentUser }) {
  // Vérifier si l'utilisateur est connecté
  if (!currentUser) {
    return (
      <div className="roles-management-container">
        <div className="no-user-message">
          <div className="no-user-icon">
            <Lock size={48} />
          </div>
          <h2>Utilisateur non connecté</h2>
          <p>Veuillez vous connecter pour accéder à la gestion des rôles.</p>
        </div>
      </div>
    );
  }

  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [roleToEdit, setRoleToEdit] = useState(null);
  const [copiedPermissions, setCopiedPermissions] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [usingAdvanced, setUsingAdvanced] = useState(false);
  const { hasPermission, loading: permissionsLoading, permissions, usingAdvancedSystem } = useAdvancedPermissions({ userId: currentUser.id });

  // Vérifier si l'utilisateur a la permission d'écriture sur les rôles
  const canEditRoles = hasPermission({ resource: "roles", action: "write" });

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        // Utiliser uniquement le nouveau système advanced_roles
        const advancedRoles = await AdvancedRolesService.getAllRoles();
        if (advancedRoles.length > 0) {
          setRoles(advancedRoles);
          setUsingAdvanced(true);
        } else {
          // Si aucun rôle n'existe, initialiser les rôles par défaut
          console.log("Aucun rôle trouvé, initialisation des rôles par défaut...");
          try {
            await AdvancedRolesService.initializeAdvancedRoles(currentUser?.id);
            const newRoles = await AdvancedRolesService.getAllRoles();
            setRoles(newRoles);
            setUsingAdvanced(true);
          } catch (initError) {
            console.error("Erreur lors de l'initialisation des rôles:", initError);
            setError("Erreur lors de l'initialisation des rôles par défaut");
          }
        }
      } catch (error) {
        console.error("Erreur lors du chargement des rôles:", error);
        setError("Erreur lors du chargement des rôles");
      }
    };

    fetchRoles();
  }, [currentUser?.id]);

  const handleSaveRole = useCallback(async () => {
    if (!selectedRole) return;

    if (!canEditRoles) {
      setError("Vous n'avez pas les permissions nécessaires pour modifier les rôles");
      return;
    }

    if (!currentUser?.id) {
      setError("Utilisateur non connecté");
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await AdvancedRolesService.updateRole(selectedRole.id, selectedRole, currentUser.id);

      setRoles((prev) =>
        prev.map((role) => (role.id === selectedRole.id ? selectedRole : role)),
      );
      setSuccess("Les permissions ont été enregistrées avec succès");
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("Erreur lors de l'enregistrement des permissions:", error);
      setError("Erreur lors de l'enregistrement des permissions");
    } finally {
      setIsSaving(false);
    }
  }, [selectedRole, canEditRoles, roles, currentUser?.id]);

  const handleAddRole = useCallback(
    async (newRole) => {
      if (!canEditRoles) {
        setError("Vous n'avez pas les permissions nécessaires pour ajouter des rôles");
        return;
      }

      try {
        await AdvancedRolesService.createRole(newRole);

        setRoles((prev) => [...prev, newRole]);
        setSuccess("Le rôle a été ajouté avec succès");
        setTimeout(() => setSuccess(null), 3000);
      } catch (error) {
        console.error("Erreur lors de l'ajout du rôle:", error);
        setError("Erreur lors de l'ajout du rôle");
      }
    },
    [canEditRoles],
  );

  const handleEditRole = useCallback((role) => {
    setRoleToEdit(role);
    setIsEditModalOpen(true);
  }, []);

  const handleUpdateRole = useCallback(
    async (updatedRole) => {
      if (!canEditRoles) {
        setError("Vous n'avez pas les permissions nécessaires pour modifier les rôles");
        return;
      }

      if (!currentUser?.id) {
        setError("Utilisateur non connecté");
        return;
      }

      try {
        await AdvancedRolesService.updateRole(updatedRole.id, updatedRole, currentUser.id);

        setRoles((prev) =>
          prev.map((role) => (role.id === updatedRole.id ? updatedRole : role)),
        );
        if (selectedRole?.id === updatedRole.id) {
          setSelectedRole(updatedRole);
        }
        setSuccess("Le rôle a été modifié avec succès");
        setTimeout(() => setSuccess(null), 3000);
      } catch (error) {
        console.error("Erreur lors de la modification du rôle:", error);
        setError("Erreur lors de la modification du rôle");
      }
    },
    [canEditRoles, selectedRole, currentUser?.id],
  );

  const handleCopyPermissions = () => {
    if (!selectedRole) return;
    setCopiedPermissions(selectedRole.permissions);
    setSuccess("Permissions copiées vers le presse-papiers");
    setTimeout(() => setSuccess(null), 3000);
  };

  const handlePastePermissions = () => {
    if (!selectedRole || !copiedPermissions) return;

    if (!canEditRoles) {
      setError("Vous n'avez pas les permissions nécessaires pour modifier les rôles");
      return;
    }

    setSelectedRole((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        permissions: { ...copiedPermissions },
      };
    });

    setSuccess("Permissions collées depuis le presse-papiers");
    setTimeout(() => setSuccess(null), 3000);
  };

  const handlePermissionChange = useCallback((resource, action, value) => {
    if (!selectedRole || !canEditRoles) return;

    setSelectedRole((prev) => {
      if (!prev) return prev;

      const newPermissions = { ...prev.permissions[resource] };
      newPermissions[action] = value;

      // Validation des dépendances hiérarchiques
      if (value) {
        // Si on active write, create, delete ou manage, on active automatiquement read
        if ((action === 'write' || action === 'create' || action === 'delete' || action === 'manage') && !newPermissions.read) {
          newPermissions.read = true;
        }
        // Si on active manage, on active automatiquement toutes les autres actions
        if (action === 'manage') {
          newPermissions.write = true;
          newPermissions.create = true;
          newPermissions.delete = true;
        }
      } else {
        // Si on désactive read, on désactive automatiquement toutes les autres actions
        if (action === 'read') {
          newPermissions.write = false;
          newPermissions.create = false;
          newPermissions.delete = false;
          newPermissions.manage = false;
        }
        // Si on désactive write, create ou delete, on vérifie si manage doit être désactivé
        else if (action === 'write' || action === 'create' || action === 'delete') {
          if (newPermissions.manage && (!newPermissions.write || !newPermissions.create || !newPermissions.delete)) {
            newPermissions.manage = false;
          }
        }
      }

      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          [resource]: newPermissions,
        },
      };
    });
  }, [selectedRole, canEditRoles]);

  return (
    <div className="roles-management-container">
      {/* Header Section */}
      <div className="roles-header">
        <div className="roles-header-content">
          <div className="flex items-center">
            <div className="roles-header-icon">
              <Shield size={32} />
            </div>
            <div className="roles-header-text">
              <h1>Gestion Avancée des Rôles</h1>
              <p>Configuration professionnelle des permissions et accès utilisateurs</p>
            </div>
          </div>

          <Button
            onClick={() => setIsAddModalOpen(true)}
            className="roles-button primary"
          >
            <Plus size={16} />
            Nouveau Rôle
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="roles-alert error">
          <AlertTriangle size={16} className="inline mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="roles-alert success">
          <Check size={16} className="inline mr-2" />
          {success}
        </div>
      )}

      {/* Main Content */}
      <div className="roles-grid">
        {/* Roles List Sidebar */}
        <div className="roles-sidebar">
          <h2>Rôles Disponibles</h2>
          <div className="roles-list">
            {roles.map((role) => (
              <div
                key={role.id}
                className={`role-card ${selectedRole?.id === role.id ? 'selected' : ''}`}
                onClick={() => setSelectedRole(role)}
              >
                <div className="role-card-header">
                  <div className="flex items-center">
                    <div className={`role-card-icon ${role.name.toLowerCase().includes('admin') ? 'admin' : role.name.toLowerCase().includes('lecture') ? 'readonly' : 'custom'}`}>
                      {role.name.toLowerCase().includes('admin') ? <Crown size={16} /> :
                        role.name.toLowerCase().includes('lecture') ? <Eye size={16} /> :
                          <Settings size={16} />}
                    </div>
                    <span className="role-card-title">{role.name}</span>
                  </div>
                  <span className="role-card-badge">
                    {role.name.toLowerCase().includes('admin') ? 'Admin' :
                      role.name.toLowerCase().includes('lecture') ? 'Lecture seule' :
                        'Personnalisé'}
                  </span>
                </div>
                {role.description && (
                  <p className="role-card-description">{role.description}</p>
                )}
                {canEditRoles && (
                  <button
                    className="role-card-edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditRole(role);
                    }}
                    title="Modifier le rôle"
                  >
                    <Edit2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Permissions Panel */}
        <div className="roles-main">
          {selectedRole ? (
            <>
              <div className="roles-main-header">
                <div className="roles-main-title">
                  <h2>Gestion des Permissions</h2>
                  <p className="role-name">{selectedRole.name}</p>
                  {selectedRole.description && (
                    <p className="text-gray-600">{selectedRole.description}</p>
                  )}
                </div>
                <div className="roles-main-actions">
                  <button
                    className="roles-button"
                    onClick={handleCopyPermissions}
                    disabled={!selectedRole}
                    title="Copier les permissions"
                  >
                    <Copy size={16} />
                    Copier
                  </button>
                  <button
                    className="roles-button"
                    onClick={handlePastePermissions}
                    disabled={!copiedPermissions || !selectedRole}
                    title="Coller les permissions"
                  >
                    <ClipboardPaste size={16} />
                    Coller
                  </button>
                  <button
                    className="roles-button"
                    onClick={() => setSelectedRole(null)}
                    title="Annuler la sélection"
                  >
                    <X size={16} />
                    Annuler
                  </button>
                  <button
                    className="roles-button primary"
                    onClick={handleSaveRole}
                    disabled={!canEditRoles || isSaving}
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        Enregistrer
                      </>
                    )}
                  </button>
                </div>
              </div>

              <AdvancedPermissionTable
                role={selectedRole}
                onPermissionChange={handlePermissionChange}
                disabled={!canEditRoles}
              />
            </>
          ) : (
            <div className="no-role-selected">
              <div className="no-role-icon">
                <Shield size={32} />
              </div>
              <h3 className="no-role-title">Aucun rôle sélectionné</h3>
              <p className="no-role-description">
                Sélectionnez un rôle dans la liste pour visualiser et modifier ses permissions
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <AddRoleModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddRole}
      />

      <EditRoleModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        role={roleToEdit}
        onSave={handleUpdateRole}
      />
    </div>
  );
}

export default AdvancedRolesManagementPage;
